import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import SignatureCanvas from 'react-signature-canvas'
import ConfigLayout from '../components/configLayout'
import { useStyles } from './configureEvidences.styles'
import { ConfigureEvidencesProps, EvidenceType, PlanEvidence, UploadedEvidenceFile } from '../types'
import { DEFAULT_PLAN_EVIDENCES, PLAN_EVIDENCE_TYPE_TO_ID } from '../constants'
import { usePlanEvidenceFiles } from '../hooks/usePlanEvidenceFiles'
import { icons } from '../../../../../utils/icons'

type ViewMode = 'list' | 'grid'

interface EvidenceTypeConfig {
  accept: Record<string, string[]>
  formats: string
  multiple: boolean
}

const EVIDENCE_TYPE_CONFIG: Record<EvidenceType, EvidenceTypeConfig> = {
  image: {
    accept: { 'image/*': [] },
    formats: 'PNG, JPG, JPEG',
    multiple: true,
  },
  video: {
    accept: { 'video/*': [] },
    formats: 'MP4, WMV, WMA',
    multiple: true,
  },
  sign: {
    accept: { 'image/*': [] },
    formats: 'PNG, JPG, JPEG',
    multiple: true,
  },
  document: {
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': [],
      'text/plain': [],
      'text/csv': [],
    },
    formats: 'XLSX, PDF, TXT, DOCX, PPTX y CSV',
    multiple: true,
  },
  comment: {
    accept: {},
    formats: '',
    multiple: false,
  },
}

const cleanFileName = (fileName: string) => fileName.replace(/\s+/g, '')

const isImageEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === PLAN_EVIDENCE_TYPE_TO_ID.image

const isVideoEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === PLAN_EVIDENCE_TYPE_TO_ID.video

const isDocumentEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === PLAN_EVIDENCE_TYPE_TO_ID.document

const isSignEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === PLAN_EVIDENCE_TYPE_TO_ID.sign

const ConfigureEvidences = ({ evidences, onSave, onBack }: ConfigureEvidencesProps) => {
  const { t } = useTranslation()
  const styles = useStyles()

  const { uploadFile, deleteFile } = usePlanEvidenceFiles()
  const uploadNameRef = useRef(`manual-action-plan/${Date.now()}`)
  const uploadedKeysInSessionRef = useRef<Set<string>>(new Set())
  const uploadPromisesRef = useRef<Promise<unknown>[]>([])

  const [localEvidences, setLocalEvidences] = useState<PlanEvidence[]>(evidences)
  const [selectedTypeId, setSelectedTypeId] = useState<EvidenceType>(
    evidences[0]?.id ?? 'image'
  )
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [commentText, setCommentText] = useState('')
  const signRef = useRef<SignatureCanvas | null>(null)
  const [isSignEmpty, setIsSignEmpty] = useState(true)
  const [isCleaning, setIsCleaning] = useState(false)

  const selectedEvidence = useMemo(
    () =>
      (localEvidences.find((evidence) => evidence.id === selectedTypeId) ??
        localEvidences[0] ??
        DEFAULT_PLAN_EVIDENCES[0]) as PlanEvidence,
    [localEvidences, selectedTypeId]
  )

  const selectedTypeConfig = EVIDENCE_TYPE_CONFIG[selectedTypeId]

  const isCommentType = selectedTypeId === 'comment'
  const isSignType = selectedTypeId === 'sign'

  const canvasToFile = async (canvas: HTMLCanvasElement, fileName: string): Promise<File> => {
    const dataUrl = canvas.toDataURL('image/png')
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    return new File([blob], fileName, { type: 'image/png' })
  }

  const filePreviewUrls = useMemo(() => {
    return selectedEvidence.files.map((file) => {
      if (file.url) return file.url
      if (file.file) return URL.createObjectURL(file.file)
      return ''
    })
  }, [selectedEvidence.files])

  useEffect(() => {
    return () => {
      filePreviewUrls.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
    }
  }, [filePreviewUrls])

  const handleSelectType = (typeId: EvidenceType) => {
    setSelectedTypeId(typeId)
    setCommentText('')
  }

  const evidenceTypeIdFor = useCallback((typeId: EvidenceType) => {
    return PLAN_EVIDENCE_TYPE_TO_ID[typeId]
  }, [])

  const createUploadingFile = useCallback(
    (file: File, typeId: EvidenceType): UploadedEvidenceFile => {
      const evidenceTypeId = evidenceTypeIdFor(typeId)
      const name = cleanFileName(file.name)
      const key = `${uploadNameRef.current}/${name}`
      return {
        name,
        url: '',
        key,
        evidenceTypeId,
        isUploading: true,
        file,
      }
    },
    [evidenceTypeIdFor]
  )

  const handleUploadResult = useCallback(
    (typeId: EvidenceType, name: string, result: UploadedEvidenceFile | null) => {
      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== typeId) return evidence
          return {
            ...evidence,
            files: evidence.files.map((file) => {
              if (file.name !== name) return file
              if (result) {
                return {
                  ...result,
                  isUploading: false,
                  file: undefined,
                }
              }
              return {
                ...file,
                isUploading: false,
                error: t('actionPlan.manualCreation.evidences.uploadError'),
              }
            }),
          }
        })
      )
    },
    [t]
  )

  const uploadSingleFile = useCallback(
    async (file: File, typeId: EvidenceType, uploadingFile: UploadedEvidenceFile) => {
      uploadedKeysInSessionRef.current.add(uploadingFile.key)

      const promise = uploadFile(file, uploadingFile.evidenceTypeId, uploadNameRef.current)
        .then((result) => {
          handleUploadResult(typeId, uploadingFile.name, result)
        })
        .finally(() => {
          uploadPromisesRef.current = uploadPromisesRef.current.filter((p) => p !== promise)
        })

      uploadPromisesRef.current.push(promise)
      return promise
    },
    [uploadFile, handleUploadResult]
  )

  const handleAddFiles = useCallback(
    async (acceptedFiles: File[]) => {
      const typeId = selectedTypeId

      const uploadingFiles = acceptedFiles.map((file) => createUploadingFile(file, typeId))

      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== typeId) return evidence
          const currentFiles = selectedTypeConfig.multiple ? evidence.files : []
          return { ...evidence, files: [...currentFiles, ...uploadingFiles] }
        })
      )

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i]
        const uploadingFile = uploadingFiles[i]
        await uploadSingleFile(file, typeId, uploadingFile)
      }
    },
    [selectedTypeId, selectedTypeConfig.multiple, createUploadingFile, uploadSingleFile]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: isCommentType ? undefined : selectedTypeConfig.accept,
    multiple: selectedTypeConfig.multiple,
    onDrop: handleAddFiles,
    disabled: isCommentType,
  })

  const handleRemoveFile = useCallback(
    async (fileIndex: number) => {
      const file = selectedEvidence.files[fileIndex]
      if (!file) return

      if (file.url) {
        await deleteFile(file.key)
        uploadedKeysInSessionRef.current.delete(file.key)
      }

      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== selectedTypeId) return evidence
          return {
            ...evidence,
            files: evidence.files.filter((_, index) => index !== fileIndex),
          }
        })
      )
    },
    [selectedEvidence.files, selectedTypeId, deleteFile]
  )

  const handleRetryUpload = useCallback(
    async (fileIndex: number) => {
      const file = selectedEvidence.files[fileIndex]
      if (!file?.file) return

      const typeId = selectedTypeId

      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== typeId) return evidence
          return {
            ...evidence,
            files: evidence.files.map((item, index) =>
              index === fileIndex ? { ...item, isUploading: true, error: undefined } : item
            ),
          }
        })
      )

      await uploadSingleFile(file.file, typeId, {
        ...file,
        isUploading: true,
        error: undefined,
      })
    },
    [selectedEvidence.files, selectedTypeId, uploadSingleFile]
  )

  const handleAddComment = () => {
    const trimmedComment = commentText.trim()
    if (!trimmedComment) return
    setLocalEvidences((prev) =>
      prev.map((evidence) => {
        if (evidence.id !== selectedTypeId) return evidence
        return { ...evidence, comments: [...evidence.comments, trimmedComment] }
      })
    )
    setCommentText('')
  }

  const handleRemoveComment = (commentIndex: number) => {
    setLocalEvidences((prev) =>
      prev.map((evidence) => {
        if (evidence.id !== selectedTypeId) return evidence
        return {
          ...evidence,
          comments: evidence.comments.filter((_, index) => index !== commentIndex),
        }
      })
    )
  }

  const hasAnyEvidence = useMemo(
    () =>
      localEvidences.some(
        (evidence) => evidence.files.length > 0 || evidence.comments.length > 0
      ),
    [localEvidences]
  )

  const hasUploadingFiles = useMemo(
    () => localEvidences.some((evidence) => evidence.files.some((file) => file.isUploading)),
    [localEvidences]
  )

  const hasErrorFiles = useMemo(
    () => localEvidences.some((evidence) => evidence.files.some((file) => file.error)),
    [localEvidences]
  )

  const handleSave = () => {
    if (hasUploadingFiles || hasErrorFiles) return
    onSave(localEvidences)
  }

  const handleBack = async () => {
    await Promise.all(uploadPromisesRef.current)

    const keysToDelete = Array.from(uploadedKeysInSessionRef.current)
    if (keysToDelete.length > 0) {
      setIsCleaning(true)
      await Promise.all(keysToDelete.map((key) => deleteFile(key)))
      uploadedKeysInSessionRef.current.clear()
      setIsCleaning(false)
    }
    onBack()
  }

  const handleAddSignature = async () => {
    const canvas = signRef.current?.getCanvas()
    if (!canvas || isSignEmpty) return
    const file = await canvasToFile(canvas, `firma_${Date.now()}.png`)
    const typeId = selectedTypeId
    const uploadingFile = createUploadingFile(file, typeId)

    setLocalEvidences((prev) =>
      prev.map((evidence) => {
        if (evidence.id !== typeId) return evidence
        return { ...evidence, files: [...evidence.files, uploadingFile] }
      })
    )

    signRef.current?.clear()
    setIsSignEmpty(true)

    await uploadSingleFile(file, typeId, uploadingFile)
  }

  const handleClearSignature = () => {
    signRef.current?.clear()
    setIsSignEmpty(true)
  }

  const renderFileState = (file: UploadedEvidenceFile, variant: 'list' | 'grid' = 'list') => {
    const isGrid = variant === 'grid'

    if (file.isUploading) {
      return (
        <Box sx={isGrid ? styles.fileStateGrid : styles.fileState}>
          <CircularProgress size={16} />
          <Typography sx={styles.fileStateText}>
            {t('actionPlan.manualCreation.evidences.uploading')}
          </Typography>
        </Box>
      )
    }

    if (file.error) {
      return (
        <Box sx={isGrid ? styles.fileStateGridError : styles.fileStateError}>
          <Typography sx={styles.fileErrorText}>{file.error}</Typography>
        </Box>
      )
    }

    return null
  }

  const renderFileList = () => {
    if (viewMode === 'grid') {
      const isDocumentType = isDocumentEvidence(selectedEvidence.files[0]?.evidenceTypeId ?? 0)

      return (
        <Box sx={styles.filesGrid}>
          {selectedEvidence.files.map((file, index) => (
            <Box
              key={`${file.name}-${index}`}
              sx={[
                isDocumentType ? styles.fileCardGridDocument : styles.fileCardGrid,
                file.error ? styles.fileCardError : {},
              ]}
            >
              {file.isUploading || file.error ? (
                <>
                  {renderFileState(file, 'grid')}
                  <Box sx={styles.fileGridActionsOverlay}>
                    {file.error && (
                      <IconButton
                        sx={styles.retryButton}
                        onClick={() => handleRetryUpload(index)}
                        aria-label={t('actionPlan.manualCreation.evidences.retry')}
                      >
                        {icons('refresh')}
                      </IconButton>
                    )}
                    {!file.isUploading && (
                      <IconButton
                        sx={styles.deleteButtonGrid}
                        onClick={() => handleRemoveFile(index)}
                        aria-label={t('general.delete')}
                      >
                        {icons('delete')}
                      </IconButton>
                    )}
                  </Box>
                </>
              ) : isImageEvidence(file.evidenceTypeId) || isSignEvidence(file.evidenceTypeId) ? (
                <>
                  <Box
                    component='img'
                    src={filePreviewUrls[index]}
                    alt={file.name}
                    sx={styles.filePreviewGrid}
                  />
                  <Box sx={styles.filePreviewGridOverlay} data-overlay='true'>
                    <IconButton
                      sx={styles.deleteButtonGridHover}
                      onClick={(event) => {
                        event.stopPropagation()
                        handleRemoveFile(index)
                      }}
                      aria-label={t('general.delete')}
                      disableRipple
                    >
                      {icons('delete')}
                    </IconButton>
                  </Box>
                </>
              ) : isVideoEvidence(file.evidenceTypeId) ? (
                <>
                  <Box
                    component='video'
                    src={filePreviewUrls[index]}
                    preload='metadata'
                    muted
                    aria-label={file.name}
                    sx={styles.filePreviewGrid}
                  />
                  <Box sx={styles.filePreviewGridOverlay} data-overlay='true'>
                    <IconButton
                      sx={styles.deleteButtonGridHover}
                      onClick={(event) => {
                        event.stopPropagation()
                        handleRemoveFile(index)
                      }}
                      aria-label={t('general.delete')}
                      disableRipple
                    >
                      {icons('delete')}
                    </IconButton>
                  </Box>
                </>
              ) : isDocumentEvidence(file.evidenceTypeId) ? (
                <>
                  <Box sx={styles.fileIconDocument}>{icons('paper')}</Box>
                  <Box sx={styles.fileNameWithAction}>
                    <Typography sx={styles.fileNameGrid} title={file.name}>
                      {file.name}
                    </Typography>
                    <IconButton
                      sx={styles.deleteButtonGrid}
                      onClick={() => handleRemoveFile(index)}
                      aria-label={t('general.delete')}
                    >
                      {icons('delete')}
                    </IconButton>
                  </Box>
                </>
              ) : null}
            </Box>
          ))}
        </Box>
      )
    }

    return (
      <Box sx={styles.filesList}>
        {selectedEvidence.files.map((file, index) => (
          <Box
            key={`${file.name}-${index}`}
            sx={[styles.fileCardList, file.error ? styles.fileCardListError : {}]}
          >
            <Typography sx={styles.fileNameList} title={file.name}>
              {file.name}
            </Typography>
            {renderFileState(file, 'list')}
            <Box sx={styles.fileActions}>
              {file.error && (
                <IconButton
                  sx={styles.retryButton}
                  onClick={() => handleRetryUpload(index)}
                  aria-label={t('actionPlan.manualCreation.evidences.retry')}
                >
                  {icons('refresh')}
                </IconButton>
              )}
              {!file.isUploading && (
                <IconButton
                  sx={styles.deleteButton}
                  onClick={() => handleRemoveFile(index)}
                  aria-label={t('general.delete')}
                >
                  {icons('delete')}
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  const renderCommentsList = () => (
    <Box sx={styles.commentsList}>
      {selectedEvidence.comments.map((comment, index) => (
        <Box key={`comment-${index}`} sx={styles.commentCard}>
          <Box sx={styles.commentIcon}>{icons('chatBubbleOutlineOutlined')}</Box>
          <Typography sx={styles.commentText}>{comment}</Typography>
          <IconButton
            sx={styles.deleteButton}
            onClick={() => handleRemoveComment(index)}
            aria-label={t('general.delete')}
          >
            {icons('delete')}
          </IconButton>
        </Box>
      ))}
    </Box>
  )

  const renderRightContent = () => {
    if (isCommentType) {
      return (
        <Box sx={styles.rightContent}>
          <Box sx={styles.commentInput}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              placeholder={t('actionPlan.manualCreation.evidences.commentPlaceholder')}
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
              sx={styles.commentTextField}
              variant='outlined'
            />
            <Box
              role='button'
              tabIndex={0}
              aria-disabled={!commentText.trim()}
              sx={[
                styles.commentAddButton,
                !commentText.trim() && styles.commentAddButtonDisabled,
              ]}
              onClick={() => {
                if (commentText.trim()) handleAddComment()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  if (commentText.trim()) handleAddComment()
                }
              }}
            >
              {t('actionPlan.manualCreation.evidences.addComment')}
            </Box>
          </Box>
          <Box sx={styles.commentsContainer}>
            {selectedEvidence.comments.length > 0 && renderCommentsList()}
          </Box>
        </Box>
      )
    }

    if (isSignType) {
      return (
        <Box sx={styles.rightContent}>
          <Box sx={styles.signatureInput}>
            <Box sx={styles.signatureCanvas}>
              <SignatureCanvas
                ref={signRef}
                penColor='#000'
                backgroundColor='#fff'
                onBegin={() => setIsSignEmpty(false)}
                canvasProps={{ className: 'sigCanvas' }}
              />
            </Box>
            <Box sx={styles.signatureActions}>
              <Box
                role='button'
                tabIndex={0}
                aria-disabled={isSignEmpty}
                sx={[
                  styles.signatureClearButton,
                  isSignEmpty && styles.signatureClearButtonDisabled,
                ]}
                onClick={() => {
                  if (!isSignEmpty) handleClearSignature()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (!isSignEmpty) handleClearSignature()
                  }
                }}
              >
                {t('actionPlan.manualCreation.evidences.clearSignature')}
              </Box>
              <Box
                role='button'
                tabIndex={0}
                aria-disabled={isSignEmpty}
                sx={[
                  styles.signatureAddButton,
                  isSignEmpty && styles.signatureAddButtonDisabled,
                ]}
                onClick={() => {
                  if (!isSignEmpty) handleAddSignature()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (!isSignEmpty) handleAddSignature()
                  }
                }}
              >
                {t('actionPlan.manualCreation.evidences.addSignature')}
              </Box>
            </Box>
          </Box>

          <Box sx={styles.filesContainer}>
            {selectedEvidence.files.length > 0 ? (
              <Box sx={styles.filesSection}>{renderFileList()}</Box>
            ) : (
              <Box sx={styles.emptyState}>
                <Typography sx={styles.emptyStateText}>
                  {t('actionPlan.manualCreation.evidences.noInformation')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )
    }

    return (
      <Box sx={styles.rightContent}>
        <Box sx={styles.dropzoneWrapper}>
          <Box
            {...getRootProps()}
            sx={[styles.dropzone, isDragActive && styles.dropzoneActive]}
          >
            <input {...getInputProps()} />
            <Box sx={styles.dropzoneContent}>
              <Box sx={styles.dropzoneIcon}>{icons('uploadFile')}</Box>
              <Typography sx={styles.dropzoneText}>
                {t('actionPlan.manualCreation.evidences.dropzoneTitle')}
              </Typography>
            </Box>
            <Typography sx={styles.dropzoneFormats}>
              {t('actionPlan.manualCreation.evidences.allowedFormats', {
                formats: selectedTypeConfig.formats,
              })}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.filesContainer}>
          {selectedEvidence.files.length > 0 ? (
            <Box sx={styles.filesSection}>{renderFileList()}</Box>
          ) : (
            <Box sx={styles.emptyState}>
              <Typography sx={styles.emptyStateText}>
                {t('actionPlan.manualCreation.evidences.noInformation')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <ConfigLayout
      title={t('actionPlan.manualCreation.evidences.configureTitle')}
      subtitle={t('actionPlan.manualCreation.evidences.configureSubtitle')}
      onBack={handleBack}
      onSave={handleSave}
      saveDisabled={!hasAnyEvidence || hasUploadingFiles || hasErrorFiles || isCleaning}
      saveLabel={t('actionPlan.manualCreation.evidences.saveAndBack')}
      backLabel={t('actionPlan.manualCreation.evidences.back')}
    >
      <Box sx={styles.content}>
        <Box sx={styles.leftPanel}>
          <Typography sx={styles.panelHeader}>
            {t('actionPlan.manualCreation.evidences.panelTitle')}
          </Typography>
          <Box sx={styles.typesList}>
            {localEvidences.map((evidence) => {
              const isSelected = evidence.id === selectedTypeId
              return (
                <Box
                  key={evidence.id}
                  role='button'
                  aria-pressed={isSelected}
                  tabIndex={0}
                  onClick={() => handleSelectType(evidence.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleSelectType(evidence.id)
                    }
                  }}
                  sx={[styles.typeRow, isSelected && styles.typeRowActive]}
                >
                  <Typography sx={styles.typeLabel}>{t(evidence.label)}</Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        <Box sx={styles.rightPanel}>
          <Box sx={styles.rightHeader}>
            <Typography sx={styles.rightTitle}>{t(selectedEvidence.label)}</Typography>
            {!isCommentType && (
              <Box sx={styles.viewToggle}>
                <Box
                  role='button'
                  tabIndex={0}
                  sx={[styles.toggleButton, viewMode === 'list' && styles.toggleButtonActive]}
                  onClick={() => setViewMode('list')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setViewMode('list')
                    }
                  }}
                  aria-pressed={viewMode === 'list'}
                >
                  {icons('list')}
                  <Typography sx={styles.toggleLabel}>
                    {t('actionPlan.manualCreation.evidences.list')}
                  </Typography>
                </Box>
                <Box
                  role='button'
                  tabIndex={0}
                  sx={[styles.toggleButton, viewMode === 'grid' && styles.toggleButtonActive]}
                  onClick={() => setViewMode('grid')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setViewMode('grid')
                    }
                  }}
                  aria-pressed={viewMode === 'grid'}
                >
                  {icons('mosaic')}
                  <Typography sx={styles.toggleLabel}>
                    {t('actionPlan.manualCreation.evidences.grid')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {renderRightContent()}
        </Box>
      </Box>
    </ConfigLayout>
  )
}

export default ConfigureEvidences
